import cx from "classnames";

import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { ModalSelector } from "molecules/containers/modal-selector-list/entry/ModalSelector";
import { ListModalSize } from "molecules/modal-window/list-modal/ListModal";
import { ListModal } from "molecules/modal-window/list-modal/ListModal";
import { formatCurrency } from "toolbox/Money";

type BaseSelectModalProps = {
  open?: boolean;
  onClose: () => void;
  title: string;
  subtitle?: React.ReactNode;
  size?: ListModalSize;
  type: "sort" | "filter" | "products" | "location" | undefined;
  selectionType?: "single" | "multiple" | undefined;
  items: {
    id: string;
    title: string;
    priceOrResults?: number | undefined;
    membershipPrice?: number | null;
    description?: string | null;
    subtitle?: string;
    disabled?: boolean;
    tagText?: string | null;
    tagColor?: string | null;
    showWarning?: boolean;
  }[];
  checkedItems: string[] | string;
  onSelect: (value: string) => void;
  onFirstButtonClick: () => void;
  firstButtonLabel: string;
  firstButtonDisabled?: boolean;
  onSecondButtonClick?: undefined;
  secondButtonLabel?: undefined;
  secondButtonDisabled?: boolean;
  isVariantsModal?: boolean;
  errorMessage?: string;
  displayError?: boolean;
  hasInvalidItemLocation?: boolean;
};

type WithSecondButtonProps = {
  onSecondButtonClick: () => void;
  secondButtonLabel: string;
} & Omit<BaseSelectModalProps, "secondButtonLabel" | "onSecondButtonClick">;

type SelectModalProps = BaseSelectModalProps | WithSecondButtonProps;

export const ModalSelectorList = ({
  open,
  onClose,
  title,
  subtitle,
  type,
  selectionType,
  size,
  items,
  checkedItems,
  onSelect,
  onFirstButtonClick,
  firstButtonLabel,
  firstButtonDisabled,
  onSecondButtonClick,
  secondButtonLabel,
  secondButtonDisabled,
  isVariantsModal = false,
  errorMessage,
  displayError = false,
}: SelectModalProps) => {
  const setLabel = (
    membershipPrice: number | null | undefined,
    priceOrResults: number | undefined
  ) => {
    const initialLabel = isVariantsModal ? "From " : "";
    if (type === "products" && priceOrResults) {
      if (membershipPrice) {
        return (
          <>
            {initialLabel}
            <span className="text-body2">
              {formatCurrency(priceOrResults)} |{" "}
            </span>
            <span className="text-body2 text-primary">
              {formatCurrency(membershipPrice)} Member
            </span>
          </>
        );
      }
      return (
        <>
          {initialLabel}
          <span className="text-body2">{formatCurrency(priceOrResults)}</span>
        </>
      );
    } else if (type === "filter") {
      return (
        <>
          {initialLabel}
          <span className="text-body2">
            {priceOrResults ?? 0}{" "}
            {(priceOrResults ?? 0) === 1 ? "result" : "results"}
          </span>
        </>
      );
    }
  };

  const modalProps = {
    open,
    onClose,
    size,
    title,
    subtitle,
    firstButtonLabel,
    onFirstButtonClick,
    firstButtonDisabled,
    titleClassName:
      type === "location" ? "text-center whitespace-nowrap" : undefined,
  };

  const content = (
    <>
      {subtitle && <div className="p-4 pb-3 text-center">{subtitle}</div>}
      <div
        className={cx(
          "px-4",
          type === "location" && "max-h-[400px] overflow-y-auto"
        )}
      >
        {items?.map((item) => {
          return (
            <ModalSelector
              key={item.id}
              tag={
                item.tagText && (
                  <DescriptiveTag
                    color={item.tagColor ? "custom" : "primary"}
                    size="small"
                    customColor={item.tagColor}
                  >
                    {item.tagText}
                  </DescriptiveTag>
                )
              }
              title={item.title || ""}
              checked={checkedItems?.includes(item.id)}
              description={item.description}
              onChange={() => {
                onSelect(item.id);
              }}
              label={setLabel(item.membershipPrice, item.priceOrResults)}
              type={
                type === "filter"
                  ? "checkbox"
                  : selectionType === "multiple"
                  ? "checkbox"
                  : "radio"
              }
              subtitle={item.subtitle}
              disabled={item.disabled}
              isLocationType={type === "location"}
              showWarning={item.showWarning}
            />
          );
        })}
      </div>
      {displayError && errorMessage && (
        <div className="mt-2 px-7 pb-4">
          <div className="text-sm font-semibold text-red">{errorMessage}</div>
        </div>
      )}
    </>
  );

  if (secondButtonLabel && onSecondButtonClick) {
    return (
      <ListModal
        {...modalProps}
        secondButtonLabel={secondButtonLabel}
        onSecondButtonClick={onSecondButtonClick}
        secondButtonDisabled={secondButtonDisabled}
      >
        {content}
      </ListModal>
    );
  }
  return <ListModal {...modalProps}>{content}</ListModal>;
};
